// src/hooks/useAgents.js
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc,
  deleteDoc, doc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

export function useAgents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const statusCache = useRef({});

  // Firestore realtime listener
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'whatsapp_agent_session'), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAgents(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  // Poll /health for each agent every 30 seconds
  const fetchAgentHealth = useCallback(async (agent) => {
    if (!agent.domain) return null;
    const base = agent.domain.replace(/\/$/, '');
    try {
      const res = await fetch(`${base}/health`, {
        signal: AbortSignal.timeout(8000)
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      return { online: true, ...data };
    } catch {
      return { online: false, whatsapp_ready: false };
    }
  }, []);

  const [statuses, setStatuses] = useState({});

  const pollAll = useCallback(async (agentList) => {
    const results = await Promise.allSettled(
      agentList.map(a => fetchAgentHealth(a).then(s => ({ id: a.id, status: s })))
    );
    const updated = {};
    results.forEach(r => {
      if (r.status === 'fulfilled' && r.value) {
        updated[r.value.id] = r.value.status;
      }
    });
    setStatuses(prev => ({ ...prev, ...updated }));
  }, [fetchAgentHealth]);

  useEffect(() => {
    if (agents.length === 0) return;
    pollAll(agents);
    const interval = setInterval(() => pollAll(agents), 30000);
    return () => clearInterval(interval);
  }, [agents, pollAll]);

  // CRUD
  const addAgent = async ({ name, domain, token, note }) => {
    await addDoc(collection(db, 'whatsapp_agent_session'), {
      name,
      domain: domain.trim(),
      token: token.trim(),
      note: note || '',
      createdAt: serverTimestamp(),
    });
  };

  const updateAgent = async (id, data) => {
    await updateDoc(doc(db, 'whatsapp_agent_session', id), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  };

  const deleteAgent = async (id) => {
    await deleteDoc(doc(db, 'whatsapp_agent_session', id));
  };

  const refreshAgent = async (agent) => {
    const status = await fetchAgentHealth(agent);
    setStatuses(prev => ({ ...prev, [agent.id]: status }));
    return status;
  };

  return { agents, statuses, loading, addAgent, updateAgent, deleteAgent, refreshAgent };
}
